import { CampaignStoreItem, type StoreItemResponse, useStoreApi } from '@givebacks/api/store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DeepPartial } from 'core';

import { storeKeys } from '../../query-key-factory';
import { CampaignStoreItemTypes } from '../types/utils';
import { useCampaignInfoByProvider } from './utils/campaignUtils';

type UpdateCampaignRequest = DeepPartial<Omit<CampaignStoreItemTypes, 'thumbnail'>> & {
  files?: File[];
  thumbnail?: File;
};
const useUpdateCampaign = <T extends UpdateCampaignRequest>() => {
  const { causeId, campaignId } = useCampaignInfoByProvider();
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  async function updateCampaign(campaignRequest: T) {
    const { data } = await StoreApi.put<StoreItemResponse<CampaignStoreItemTypes>>(
      `/store_items/${campaignId}`,
      {
        store_item: campaignRequest,
        cause_id: causeId,
        uuid: campaignId,
      },
      {
        headers: {
          'Content-Type':
            campaignRequest.files || campaignRequest.thumbnail
              ? 'multipart/form-data'
              : 'application/json',
        },
        formSerializer: {
          visitor: function (this, value, key, path, helpers) {
            if (key === 'files') {
              this.append('store_item[files][]', value);
              return false;
            } else if (key === 'sections') {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              for (let i = 0; i < value.length; i++) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                this.append('store_item[sections][][title]', value[i].title);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/no-unsafe-member-access
                value[i].description &&
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  this.append('store_item[sections][][description]', value[i].description);
              }
              return false;
            } else if (key === 'registration_options' && path?.includes('participant_options')) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              for (let i = 0; i < value.length; i++) {
                this.append(
                  'store_item[participant_options][registration_options][][name]',
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  value[i].name
                );
                this.append(
                  'store_item[participant_options][registration_options][][required]',
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  value[i].required
                );
              }
              return false;
            }
            return helpers.defaultVisitor.call(this, value, key, path, helpers);
          },
        },
      }
    );
    return data.store_item;
  }
  return useMutation({
    mutationFn: updateCampaign,
    onSuccess: () => {
      if (!causeId || !campaignId) return;
      return queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.campaigns.id(campaignId).detail,
      });
    },
  });
};

const useFinalizeCampaign = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const { causeId } = useCampaignInfoByProvider();

  async function finalizeCampaign(campaignId: string) {
    const { data } = await StoreApi.post<{ store_item: CampaignStoreItem }>(
      `/store_items/${campaignId}/finalize?cause_id=${causeId}`,
      {
        cause_id: causeId,
        uuid: campaignId,
      }
    );
    return data.store_item;
  }
  return useMutation({
    mutationFn: finalizeCampaign,
    onSuccess: () => {
      if (!causeId) return;
      const queryKey = storeKeys.id(causeId).items.campaigns.all;
      void queryClient.invalidateQueries({ queryKey: queryKey });
      void queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.fundraisers.all,
      });
    },
  });
};

export { useFinalizeCampaign, useUpdateCampaign };
